import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ScatterChart, Scatter, ZAxis,
  FunnelChart, Funnel, LabelList, Cell
} from 'recharts';
import { FiActivity, FiCrosshair, FiTrendingUp, FiPercent, FiCalendar, FiZap, FiLayers } from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

const FUNNEL_COLORS = ['#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'];

/* ── Neon Tooltip ── */
const NeonTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(124,58,237,0.95), rgba(99,102,241,0.9))',
      backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: 12, padding: '12px 16px', boxShadow: '0 8px 32px rgba(124,58,237,0.3)',
    }}>
      <p style={{ color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: '#fff', opacity: 0.8 }} />
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{(p.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Stat Block ── */
const StatBlock = ({ icon, label, value, subtitle, accent }) => (
  <div style={{
    background: 'linear-gradient(145deg, #ffffff 0%, #F5F3FF 100%)',
    borderRadius: 16, padding: 20, position: 'relative', overflow: 'hidden',
    border: `2px solid ${accent}30`, boxShadow: `0 4px 20px ${accent}15`,
  }}>
    <div style={{
      position: 'absolute', top: -30, right: -30, width: 80, height: 80,
      borderRadius: '50%', background: `${accent}10`,
    }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10,
        background: `linear-gradient(135deg, ${accent}, ${accent}99)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 4px 12px ${accent}40`,
      }}>
        {React.cloneElement(icon, { size: 20, color: '#fff' })}
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, lineHeight: 1.1 }}>{value}</div>
      </div>
    </div>
    {subtitle && <div style={{ fontSize: 12, color: THEME.slate, fontWeight: 500 }}>{subtitle}</div>}
  </div>
);

/* ── Panel Header ── */
const PanelHeader = ({ icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
    <div style={{
      width: 38, height: 38, borderRadius: 10,
      background: `linear-gradient(135deg, ${THEME.primary}15, ${THEME.accent}10)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.primary,
    }}>
      {icon}
    </div>
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: THEME.dark, margin: 0 }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 12, color: THEME.muted, margin: 0 }}>{subtitle}</p>}
    </div>
  </div>
);

/* ── Progress Bar ── */
const ProgressBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ height: 8, background: '#E0E7FF', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${pct}%`, borderRadius: 4,
        background: `linear-gradient(90deg, ${color}, ${color}99)`,
        transition: 'width 0.8s ease',
      }} />
    </div>
  );
};

const TIME_RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

export default function CampaignPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(7);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/campaign-performance?days=${range}`)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [range]);

  const stats = data?.stats || {};
  const trendData = data?.trend || [];
  const campaignComparison = data?.campaignComparison || [];

  const totalMessages = (stats.totalSent || 0) + (stats.totalFailed || 0);
  const deliveryRate = totalMessages > 0 ? ((stats.totalSent || 0) / totalMessages * 100).toFixed(1) : 0;
  const avgPerDay = trendData.length > 0
    ? Math.round(trendData.reduce((a, d) => a + (d.campaigns || 0), 0) / trendData.length) : 0;

  /* Funnel Data */
  const funnelData = useMemo(() => [
    { name: 'Total Data', value: stats.totalMasterData || 0, fill: FUNNEL_COLORS[0] },
    { name: 'Campaigns', value: stats.totalCampaigns || 0, fill: FUNNEL_COLORS[1] },
    { name: 'Sent', value: stats.totalSent || 0, fill: FUNNEL_COLORS[2] },
    { name: 'Delivered', value: Math.round((stats.totalSent || 0) * 0.95), fill: FUNNEL_COLORS[3] },
  ].filter(d => d.value > 0), [stats]);

  /* Scatter data for engagement */
  const scatterData = useMemo(() =>
    campaignComparison.slice(0, 10).map(c => ({
      x: c.totalSent || 0,
      y: c.totalFailed || 0,
      z: (c.totalSent || 0) + (c.totalFailed || 0),
      name: c.name,
    })), [campaignComparison]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 48, height: 48, border: '4px solid #E0E7FF', borderTopColor: THEME.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiActivity size={28} style={{ color: THEME.primary }} />
            Performance Metrics
          </h1>
          <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>
            Campaign analytics over the last {range} days
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiCalendar size={18} style={{ color: THEME.primary }} />
          <select
            value={range}
            onChange={e => setRange(Number(e.target.value))}
            style={{
              padding: '10px 16px', borderRadius: 10, border: `2px solid ${THEME.accent}`,
              background: '#fff', fontSize: 13, fontWeight: 600, color: THEME.dark, cursor: 'pointer',
            }}
          >
            {TIME_RANGES.map(r => <option key={r.days} value={r.days}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginBottom: 28 }}>
        <StatBlock icon={<FiCrosshair />} label="Campaigns" value={stats.totalCampaigns || 0} subtitle={`${stats.activeCampaigns || 0} active`} accent={THEME.primary} />
        <StatBlock icon={<FiZap />} label="Delivered" value={(stats.totalSent || 0).toLocaleString()} subtitle={`${(stats.todaySent || 0).toLocaleString()} today`} accent={THEME.success} />
        <StatBlock icon={<FiPercent />} label="Success Rate" value={`${deliveryRate}%`} subtitle={`${(stats.totalFailed || 0).toLocaleString()} failed`} accent={deliveryRate >= 80 ? THEME.success : THEME.warning} />
        <StatBlock icon={<FiTrendingUp />} label="Daily Avg" value={avgPerDay} subtitle={`${trendData.length} day span`} accent={THEME.slate} />
      </div>

      {/* Main Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 28 }}>
        {/* Trend Line Chart */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
          <PanelHeader icon={<FiTrendingUp size={18} />} title="Activity Trend" subtitle="Campaigns & engagement over time" />
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="lineGrad1" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={THEME.primary} />
                    <stop offset="100%" stopColor={THEME.secondary} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#E0E7FF" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<NeonTooltip />} />
                <Legend formatter={(v) => <span style={{ color: THEME.dark, fontWeight: 600 }}>{v}</span>} />
                <Line type="monotone" dataKey="campaigns" name="Campaigns" stroke="url(#lineGrad1)" strokeWidth={3} dot={{ r: 4, fill: '#fff', stroke: THEME.primary, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="engagement" name="Engagement" stroke={THEME.success} strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.muted }}>
              <div style={{ textAlign: 'center' }}>
                <FiActivity size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontWeight: 600 }}>No trend data</div>
              </div>
            </div>
          )}
        </div>

        {/* Conversion Funnel */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
          <PanelHeader icon={<FiLayers size={18} />} title="Conversion Funnel" subtitle="Data to delivery flow" />
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <FunnelChart>
                <Tooltip content={<NeonTooltip />} />
                <Funnel dataKey="value" data={funnelData} isAnimationActive animationDuration={1200}>
                  <LabelList position="right" fill={THEME.dark} fontSize={12} fontWeight={600} formatter={(v) => v?.toLocaleString()} />
                  <LabelList position="center" fill="#fff" fontSize={11} fontWeight={700} dataKey="name" />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.muted }}>
              <div style={{ textAlign: 'center' }}>
                <FiLayers size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontWeight: 600 }}>No funnel data</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Second Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 24, marginBottom: 28 }}>
        {/* Scatter Plot */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
          <PanelHeader icon={<FiCrosshair size={18} />} title="Campaign Distribution" subtitle="Sent vs Failed analysis" />
          {scatterData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#E0E7FF" />
                <XAxis type="number" dataKey="x" name="Sent" tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="number" dataKey="y" name="Failed" tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                <ZAxis type="number" dataKey="z" range={[60, 400]} />
                <Tooltip content={<NeonTooltip />} />
                <Scatter name="Campaigns" data={scatterData} fill={THEME.primary}>
                  {scatterData.map((_, i) => (
                    <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.muted }}>
              <div style={{ textAlign: 'center', fontWeight: 600 }}>No campaign data</div>
            </div>
          )}
        </div>

        {/* Top Campaigns Bar */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
          <PanelHeader icon={<FiZap size={18} />} title="Top Campaigns" subtitle="Performance ranking" />
          {campaignComparison.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={campaignComparison.slice(0, 6).map(c => ({
                name: c.name?.slice(0, 10) || 'N/A',
                sent: c.totalSent || 0,
                failed: c.totalFailed || 0,
              }))} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#E0E7FF" horizontal={false} />
                <XAxis type="number" tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: THEME.dark, fontSize: 11, fontWeight: 600 }} axisLine={false} tickLine={false} width={70} />
                <Tooltip content={<NeonTooltip />} />
                <Bar dataKey="sent" name="Sent" fill={THEME.primary} radius={[0, 6, 6, 0]} barSize={14} />
                <Bar dataKey="failed" name="Failed" fill={THEME.danger} radius={[0, 6, 6, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.muted }}>
              <div style={{ textAlign: 'center', fontWeight: 600 }}>No campaigns yet</div>
            </div>
          )}
        </div>
      </div>

      {/* Campaign Leaderboard */}
      {campaignComparison.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
          <PanelHeader icon={<FiCrosshair size={18} />} title="Campaign Leaderboard" subtitle="Performance breakdown" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {campaignComparison.slice(0, 5).map((c, i) => {
              const total = (c.totalSent || 0) + (c.totalFailed || 0);
              const rate = total > 0 ? ((c.totalSent || 0) / total * 100) : 0;
              return (
                <div key={c._id || i} style={{
                  display: 'grid', gridTemplateColumns: '40px 1fr 100px 100px 120px',
                  alignItems: 'center', gap: 16, padding: '14px 16px',
                  background: i === 0 ? `${THEME.primary}08` : '#FAFAFF',
                  borderRadius: 12, border: i === 0 ? `2px solid ${THEME.primary}30` : '1px solid #E0E7FF',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: i < 3 ? `linear-gradient(135deg, ${FUNNEL_COLORS[i]}, ${FUNNEL_COLORS[i]}99)` : '#E0E7FF',
                    color: i < 3 ? '#fff' : THEME.muted, fontWeight: 800, fontSize: 14,
                  }}>
                    {i + 1}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: THEME.dark, fontSize: 14 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: THEME.muted }}>{c.type?.replace('_', ' ')}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: THEME.success }}>{(c.totalSent || 0).toLocaleString()}</div>
                    <div style={{ fontSize: 10, color: THEME.muted }}>SENT</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: THEME.danger }}>{(c.totalFailed || 0).toLocaleString()}</div>
                    <div style={{ fontSize: 10, color: THEME.muted }}>FAILED</div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: rate >= 80 ? THEME.success : THEME.warning }}>{rate.toFixed(1)}%</span>
                    </div>
                    <ProgressBar value={rate} max={100} color={rate >= 80 ? THEME.success : rate >= 50 ? THEME.warning : THEME.danger} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
